import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import {
  IcfCompetencyCode,
  IcfFormLevel,
  getCompetencyTitle,
  getStatementText,
} from '@/data/icfFormStatements';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { ICFProgressAPI } from '@/services/api/icfProgressAPI';
import { useIdempotencyIntent } from '@/services/idempotencyIntent';
import { trackAuthEvent } from '@/services/analytics';
import { CrfReadiness, CrfReadinessCompetency, ICFCompetency } from '@/types/icfProgress';

/**
 * ICF numbering: C1 ethics, C2 mindset, C3 agreements … C8 client growth.
 * The CRF only gates C3–C8 (C1 is a veto qualifier, C2 has no statements).
 */
export const COMPETENCY_KEY_TO_CODE: Record<ICFCompetency, string> = {
  [ICFCompetency.DEMONSTRATES_ETHICAL_PRACTICE]: 'C1',
  [ICFCompetency.EMBODIES_COACHING_MINDSET]: 'C2',
  [ICFCompetency.ESTABLISHES_MAINTAINS_AGREEMENTS]: 'C3',
  [ICFCompetency.CULTIVATES_TRUST_SAFETY]: 'C4',
  [ICFCompetency.MAINTAINS_PRESENCE]: 'C5',
  [ICFCompetency.LISTENS_ACTIVELY]: 'C6',
  [ICFCompetency.EVOKES_AWARENESS]: 'C7',
  [ICFCompetency.FACILITATES_CLIENT_GROWTH]: 'C8',
};

export function formatCrfLevel(level: string | undefined): string {
  return (level || '').toUpperCase();
}

interface GateRowProps {
  gate: CrfReadinessCompetency;
  level: IcfFormLevel;
  /** Renders the competency title above the bar. Off inside Competency Detail. */
  showTitle?: boolean;
  testID?: string;
}

/**
 * One competency gate: "4 / 5 demonstrated · 1 short" over a segmented bar.
 * Counts come from the backend verbatim (§1.4 integer table, never a percentage).
 */
export function CrfGateRow({ gate, level, showTitle = true, testID }: GateRowProps) {
  const { t, language } = useTranslation();
  const segments = Array.from({ length: gate.total }, (_, index) => index);

  return (
    <View testID={testID} style={styles.gateRow}>
      {showTitle && (
        <Text selectable style={styles.gateTitle}>
          {gate.code} · {getCompetencyTitle(level, gate.code as IcfCompetencyCode, language)}
        </Text>
      )}
      <View style={styles.barTrack} accessibilityRole="progressbar">
        {segments.map((index) => (
          <View
            key={index}
            style={[
              styles.barSegment,
              index < gate.demonstrated
                ? (gate.met ? styles.barSegmentMet : styles.barSegmentPartial)
                : styles.barSegmentEmpty,
            ]}
          />
        ))}
      </View>
      <View style={styles.gateMetaRow}>
        <Text selectable style={[styles.gateCount, gate.met ? styles.gateCountMet : styles.gateCountShort]}>
          {t('icfProgress.crfReadiness.gateCount', {
            demonstrated: gate.demonstrated,
            required: gate.required,
          })}
        </Text>
        <Text selectable style={styles.gateSeparator}>·</Text>
        {gate.met ? (
          <Text selectable style={styles.gateMet}>{t('icfProgress.crfReadiness.gateMet')}</Text>
        ) : (
          <Text selectable style={styles.gateShort}>
            {t('icfProgress.crfReadiness.gateShort', { count: gate.short })}
          </Text>
        )}
        <Text selectable style={styles.gateTotal}>
          {t('icfProgress.crfReadiness.gateTotal', { total: gate.total })}
        </Text>
      </View>
    </View>
  );
}

interface StatementChipsProps {
  level: IcfFormLevel;
  statementIds: string[];
  competencyCode: string;
}

/** Missing statement ids as chips; pressing one expands the official statement text. */
function MissingStatementChips({ level, statementIds, competencyCode }: StatementChipsProps) {
  const { t, language } = useTranslation();
  const [openId, setOpenId] = useState<string | null>(null);

  if (statementIds.length === 0) return null;

  return (
    <View style={styles.chipBlock}>
      <View style={styles.chipRow}>
        {statementIds.map((statementId) => {
          const open = openId === statementId;
          return (
            <Pressable
              key={statementId}
              testID={`crf_missing_statement_${competencyCode}_${statementId}`}
              accessibilityRole="button"
              accessibilityState={{ expanded: open }}
              accessibilityLabel={getStatementText(level, statementId, language)}
              onPress={() => setOpenId(open ? null : statementId)}
              style={({ pressed }) => [styles.chip, open && styles.chipOpen, pressed && styles.pressed]}
            >
              <Text style={[styles.chipText, open && styles.chipTextOpen]}>{statementId}</Text>
            </Pressable>
          );
        })}
      </View>
      {openId ? (
        <Text selectable style={styles.chipDetail}>
          {openId} — {getStatementText(level, openId, language)}
        </Text>
      ) : (
        <Text selectable style={styles.chipHint}>{t('icfProgress.crfReadiness.chipHint')}</Text>
      )}
    </View>
  );
}

interface Props {
  crfReadiness?: CrfReadiness | null;
  /** Called after a re-analysis fan-out so the dashboard reloads. */
  onSessionsReanalyzed?: () => Promise<void> | void;
  onCompetencyPress?: (competencyKey: ICFCompetency) => void;
  testID?: string;
}

const CODE_TO_COMPETENCY_KEY: Record<string, ICFCompetency> = Object.entries(COMPETENCY_KEY_TO_CODE)
  .reduce((acc, [key, code]) => {
    acc[code] = key as ICFCompetency;
    return acc;
  }, {} as Record<string, ICFCompetency>);

/**
 * "ICF {LEVEL} readiness (Competency Review Form rule)" — the coach-side mirror of
 * the CRF: union across sessions (§1.3) scored against the per-competency integer
 * gates (§1.4). Renders nothing until the backend sends `crf_readiness`.
 */
export function CrfReadinessCard({
  crfReadiness,
  onSessionsReanalyzed,
  onCompetencyPress,
  testID,
}: Props) {
  const { t } = useTranslation();
  const { isPhone } = useResponsive();
  const tracked = useRef(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState('');
  // ASYNC-6: re-running the same sessions at the same level is one intent, so
  // a repeated click or a retry after partial failure replays the runs that
  // already started instead of spending free re-runs or credits again.
  const reanalysisIntent = useIdempotencyIntent();

  const level = crfReadiness?.level;
  const shortTotal = crfReadiness?.statements_short_total;

  useEffect(() => {
    if (!level || tracked.current) return;
    tracked.current = true;
    trackAuthEvent('crf_readiness_viewed', {
      level,
      short_total: shortTotal ?? 0,
    });
  }, [level, shortTotal]);

  const notAssessedIds = useMemo(
    () => crfReadiness?.sessions_not_assessed_ids || [],
    [crfReadiness?.sessions_not_assessed_ids],
  );

  const runReanalysis = useCallback(async () => {
    if (!crfReadiness || notAssessedIds.length === 0) return;
    setConfirming(false);
    setReanalyzing(true);
    setNotice('');
    try {
      const { started, failed, settled, noCreditKey } = await ICFProgressAPI.analyzeSessionsAtLevel(
        notAssessedIds,
        crfReadiness.level,
        reanalysisIntent.key(`${crfReadiness.level}:${notAssessedIds.join(',')}`),
      );
      // A replayed run already finished: the next click is a new intent.
      if (settled > 0) reanalysisIntent.reset();
      const startedNotice = failed > 0
        ? t('icfProgress.crfReadiness.reanalyzePartial', { started, failed })
        : t('icfProgress.crfReadiness.reanalyzeStarted', { count: started });
      // Same no-credit copy as chat (utils/creditErrors).
      setNotice(noCreditKey ? `${startedNotice} ${t(noCreditKey)}` : startedNotice);
      trackAuthEvent('crf_readiness_reanalyze_started', {
        level: crfReadiness.level,
        sessions: started,
        failed,
      });
      ICFProgressAPI.invalidateDashboardCache();
      await onSessionsReanalyzed?.();
    } catch {
      setNotice(t('icfProgress.crfReadiness.reanalyzeError'));
    } finally {
      setReanalyzing(false);
    }
  }, [crfReadiness, notAssessedIds, onSessionsReanalyzed, reanalysisIntent, t]);

  if (!crfReadiness || !crfReadiness.level) return null;

  const levelLabel = formatCrfLevel(crfReadiness.level);
  const demonstrated = crfReadiness.verdict === 'demonstrated';

  return (
    <View testID={testID || 'crf_readiness_card'} style={styles.card}>
      <View style={[styles.header, isPhone && styles.headerPhone]}>
        <View style={styles.titleRow}>
          <View style={styles.icon}>
            <MaterialIcons name="fact-check" size={21} color={reviewDesk.accent} />
          </View>
          <View style={styles.headerCopy}>
            <Text selectable style={styles.title}>
              {t('icfProgress.crfReadiness.title', { level: levelLabel })}
            </Text>
            <Text selectable style={styles.subtitle}>
              {crfReadiness.statements_short_total > 0
                ? t('icfProgress.crfReadiness.subtitleShort', {
                  count: crfReadiness.statements_short_total,
                  level: levelLabel,
                })
                : t('icfProgress.crfReadiness.subtitleComplete', { level: levelLabel })}
            </Text>
          </View>
        </View>
        <View style={[styles.verdictChip, demonstrated ? styles.verdictChipMet : styles.verdictChipShort]}>
          <Text style={[styles.verdictText, demonstrated ? styles.verdictTextMet : styles.verdictTextShort]}>
            {demonstrated
              ? t('icfProgress.crfReadiness.verdictDemonstrated')
              : t('icfProgress.crfReadiness.verdictNotYet')}
          </Text>
        </View>
      </View>

      <Text selectable style={styles.sessionsLine}>
        {t('icfProgress.crfReadiness.sessionsConsidered', {
          count: crfReadiness.sessions_considered,
          level: levelLabel,
        })}
      </Text>

      <View style={styles.gateList}>
        {crfReadiness.per_competency.map((gate) => {
          const competencyKey = CODE_TO_COMPETENCY_KEY[gate.code];
          // The block stays a plain View: the statement chips inside are their own
          // Pressables, and nested pressables double-fire on React Native Web.
          return (
            <View key={gate.code} testID={`crf_gate_${gate.code}`} style={styles.gateBlock}>
              <CrfGateRow gate={gate} level={crfReadiness.level} />
              {!gate.met && (
                <MissingStatementChips
                  level={crfReadiness.level}
                  competencyCode={gate.code}
                  statementIds={gate.missing_statement_ids}
                />
              )}
              {!!onCompetencyPress && !!competencyKey && (
                <Pressable
                  testID={`crf_gate_${gate.code}_detail`}
                  accessibilityRole="button"
                  onPress={() => onCompetencyPress(competencyKey)}
                  style={({ pressed }) => [styles.gateLink, pressed && styles.pressed]}
                >
                  <Text style={styles.gateLinkText}>{t('icfProgress.crfReadiness.openDetail')}</Text>
                  <MaterialIcons name="chevron-right" size={16} color={reviewDesk.accent} />
                </Pressable>
              )}
            </View>
          );
        })}
      </View>

      {crfReadiness.sessions_not_assessed > 0 && (
        <View style={styles.notAssessed}>
          <Text selectable style={styles.notAssessedText}>
            {t('icfProgress.crfReadiness.notAssessed', {
              count: crfReadiness.sessions_not_assessed,
              level: levelLabel,
            })}
          </Text>
          {notAssessedIds.length > 0 && (
            confirming ? (
              <View style={styles.confirmRow}>
                <Text selectable style={styles.confirmCopy}>
                  {t('icfProgress.crfReadiness.reanalyzeConfirm', {
                    count: notAssessedIds.length,
                    level: levelLabel,
                  })}
                </Text>
                <View style={styles.confirmButtons}>
                  <Pressable
                    testID="crf_reanalyze_confirm"
                    accessibilityRole="button"
                    onPress={runReanalysis}
                    style={({ pressed }) => [styles.primaryButton, pressed && styles.pressed]}
                  >
                    <Text style={styles.primaryButtonText}>
                      {t('icfProgress.crfReadiness.reanalyzeConfirmCta')}
                    </Text>
                  </Pressable>
                  <Pressable
                    testID="crf_reanalyze_cancel"
                    accessibilityRole="button"
                    onPress={() => setConfirming(false)}
                    style={({ pressed }) => [styles.secondaryButton, pressed && styles.pressed]}
                  >
                    <Text style={styles.secondaryButtonText}>
                      {t('icfProgress.crfReadiness.reanalyzeCancel')}
                    </Text>
                  </Pressable>
                </View>
              </View>
            ) : (
              <Pressable
                testID="crf_reanalyze_cta"
                accessibilityRole="button"
                disabled={reanalyzing}
                onPress={() => setConfirming(true)}
                style={({ pressed }) => [styles.ctaButton, pressed && styles.pressed]}
              >
                {reanalyzing
                  ? <ActivityIndicator size="small" color={reviewDesk.accent} />
                  : <MaterialIcons name="refresh" size={16} color={reviewDesk.accent} />}
                <Text style={styles.ctaText}>
                  {t('icfProgress.crfReadiness.reanalyzeCta', { level: levelLabel })}
                </Text>
              </Pressable>
            )
          )}
          {!!notice && <Text selectable style={styles.notice}>{notice}</Text>}
        </View>
      )}

      <Text selectable style={styles.footnote}>
        {t('icfProgress.crfReadiness.footnote', {
          level: levelLabel,
          revision: crfReadiness.form_revision,
        })}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: { width: '100%', backgroundColor: reviewDesk.card, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, padding: 18, gap: 16 },
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 },
  headerPhone: { flexDirection: 'column' },
  titleRow: { flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 11 },
  icon: { width: 38, height: 38, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft },
  headerCopy: { flex: 1, gap: 3 },
  title: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800' },
  subtitle: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 18 },
  verdictChip: { borderRadius: 999, paddingHorizontal: 10, paddingVertical: 5 },
  verdictChipMet: { backgroundColor: reviewDesk.successSoft },
  verdictChipShort: { backgroundColor: reviewDesk.amberSoft },
  verdictText: { fontSize: 12, fontWeight: '800' },
  verdictTextMet: { color: reviewDesk.success },
  verdictTextShort: { color: reviewDesk.amber },
  sessionsLine: { color: reviewDesk.muted, fontSize: 12, lineHeight: 17 },
  gateList: { gap: 14 },
  gateBlock: { gap: 8, backgroundColor: reviewDesk.cardMuted, borderRadius: 13, padding: 13 },
  gateRow: { gap: 7 },
  gateTitle: { color: reviewDesk.ink, fontSize: 14, fontWeight: '700' },
  barTrack: { flexDirection: 'row', gap: 3 },
  barSegment: { flex: 1, height: 8, borderRadius: 4 },
  barSegmentMet: { backgroundColor: reviewDesk.success },
  barSegmentPartial: { backgroundColor: reviewDesk.amber },
  barSegmentEmpty: { backgroundColor: reviewDesk.line },
  gateMetaRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 6 },
  gateCount: { fontSize: 13, fontWeight: '800', fontVariant: ['tabular-nums'] },
  gateCountMet: { color: reviewDesk.success },
  gateCountShort: { color: reviewDesk.amber },
  gateSeparator: { color: reviewDesk.muted, fontSize: 13 },
  gateMet: { color: reviewDesk.success, fontSize: 13, fontWeight: '700' },
  gateShort: { color: reviewDesk.amber, fontSize: 13, fontWeight: '700' },
  gateTotal: { color: reviewDesk.muted, fontSize: 12 },
  chipBlock: { gap: 6 },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: { minHeight: 28, justifyContent: 'center', borderWidth: 1, borderColor: reviewDesk.lineStrong, backgroundColor: reviewDesk.card, borderRadius: 999, paddingHorizontal: 10, paddingVertical: 4 },
  chipOpen: { borderColor: reviewDesk.accent, backgroundColor: reviewDesk.accentSoft },
  chipText: { color: reviewDesk.inkSoft, fontSize: 12, fontWeight: '700', fontVariant: ['tabular-nums'] },
  chipTextOpen: { color: reviewDesk.accent },
  chipDetail: { color: reviewDesk.inkSoft, fontSize: 12, lineHeight: 18 },
  chipHint: { color: reviewDesk.muted, fontSize: 11, lineHeight: 16 },
  gateLink: { alignSelf: 'flex-start', minHeight: 32, flexDirection: 'row', alignItems: 'center', gap: 2 },
  gateLinkText: { color: reviewDesk.accent, fontSize: 12, fontWeight: '800' },
  notAssessed: { gap: 9, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: reviewDesk.line, paddingTop: 12 },
  notAssessedText: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19 },
  ctaButton: { alignSelf: 'flex-start', minHeight: 40, flexDirection: 'row', alignItems: 'center', gap: 6, borderWidth: 1, borderColor: reviewDesk.accent, borderRadius: 11, paddingHorizontal: 13, paddingVertical: 9 },
  ctaText: { color: reviewDesk.accent, fontSize: 13, fontWeight: '800' },
  confirmRow: { gap: 9, backgroundColor: reviewDesk.accentWash, borderRadius: 12, padding: 12 },
  confirmCopy: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19 },
  confirmButtons: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  primaryButton: { minHeight: 40, justifyContent: 'center', backgroundColor: reviewDesk.accent, borderRadius: 11, paddingHorizontal: 14 },
  primaryButtonText: { color: '#FFFFFF', fontSize: 13, fontWeight: '800' },
  secondaryButton: { minHeight: 40, justifyContent: 'center', borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 11, paddingHorizontal: 14 },
  secondaryButtonText: { color: reviewDesk.inkSoft, fontSize: 13, fontWeight: '700' },
  notice: { color: reviewDesk.accent, fontSize: 12, lineHeight: 17, fontWeight: '600' },
  footnote: { color: reviewDesk.muted, fontSize: 11, lineHeight: 16, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: reviewDesk.line, paddingTop: 10 },
  pressed: { opacity: 0.72 },
});

export default CrfReadinessCard;
